interface PostMessageRequest {
  key: string;
  payload: any;
  requestId?: string;
}

interface PostMessageResponse {
  key: string;
  payload: any;
  requestId: string;
  isResponse?: boolean;
}

/**
 * 发送postMessage并等待回复
 * @param target 目标窗口（window.parent 或 window.opener）
 * @param message 要发送的消息
 * @param timeout 超时时间（毫秒）
 * @param targetOrigin 目标源
 * @returns Promise<any> 返回接收到的回复消息
 */
export const postMessageWithResponse = async <T = any>(
  target: Window,
  message: PostMessageRequest,
  timeout: number = 5000,
  targetOrigin: string = "*",
  windowId?: string,
): Promise<T> => {
  // 生成唯一的请求ID
  const requestId =
    message.requestId ||
    `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return new Promise<T>((resolve, reject) => {
    // 创建消息监听器
    const messageListener = (event: MessageEvent) => {
      const data = event.data as PostMessageResponse;
      // 检查是否是我们期待的回复
      if (data && data.requestId === requestId && data.isResponse) {
        // 清理
        window.removeEventListener("message", messageListener);
        clearTimeout(timeoutId);

        // 根据消息类型处理结果
        if (data.payload.success !== false) {
          resolve(data.payload as T);
        } else {
          reject(new Error(data.payload.error || "Unknown error"));
        }
      }
    };

    // 设置超时处理
    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", messageListener);
      reject(new Error("PostMessage timeout"));
    }, timeout);

    // 添加消息监听器
    window.addEventListener("message", messageListener);

    // 发送消息
    const messageWithId = {
      ...message,
      windowId: windowId,
      requestId,
    };

    try {
      target.postMessage(messageWithId, targetOrigin);
    } catch (error) {
      // 清理
      window.removeEventListener("message", messageListener);
      clearTimeout(timeoutId);
      reject(error);
    }
  });
};
